import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Shield } from 'lucide-react';

const TermsAndConditionsPage = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center space-x-4">
            <Link to="/portal/login">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Login
              </Button>
            </Link>
            <img 
              src="/lovable-uploads/5b1ac4fa-502a-48e9-b8b6-7c1fe37496e8.png" 
              alt="smplify Logo" 
              className="h-8"
            />
            <span className="text-muted-foreground">Terms & Conditions</span>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Terms and Conditions</CardTitle>
            <p className="text-muted-foreground">
              Device Management Portal - Terms of Service
            </p>
          </CardHeader>

          <CardContent className="prose max-w-none">
            <div className="space-y-6">
              <section>
                <h3 className="text-lg font-semibold mb-3">1. Device Management Agreement</h3>
                <p>
                  By creating an account and enrolling devices in the smplify Device Management Portal, 
                  you acknowledge and agree to the following terms and conditions governing the management 
                  and monitoring of your devices.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-semibold mb-3">2. Account Registration and Approval</h3>
                <ul className="list-disc list-inside space-y-2">
                  <li>All portal accounts require administrator approval before activation</li>
                  <li>You must provide accurate and complete information during registration</li>
                  <li>Account approval is at the sole discretion of the organization's administrators</li>
                  <li>Approved accounts are subject to periodic review and may be suspended or terminated</li>
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-semibold mb-3">3. Device Enrollment and Management</h3>
                <ul className="list-disc list-inside space-y-2">
                  <li>Device enrollment requires installation of a management profile</li>
                  <li>Enrolled devices are subject to organizational security policies</li>
                  <li>Remote management capabilities include but are not limited to:
                    <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                      <li>Device lock and unlock</li>
                      <li>Remote wipe and factory reset</li>
                      <li>Location tracking and device finding</li>
                      <li>Application management and restrictions</li>
                      <li>System monitoring and compliance checking</li>
                    </ul>
                  </li>
                  <li>Each device enrollment requires individual administrative approval</li>
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-semibold mb-3">4. Data Collection and Privacy</h3>
                <ul className="list-disc list-inside space-y-2">
                  <li>Device information including model, OS version, and serial number is collected</li>
                  <li>Location data may be collected for device tracking purposes</li>
                  <li>Application usage and system performance data may be monitored</li>
                  <li>All collected data is used solely for device management and security purposes</li>
                  <li>Data handling complies with applicable privacy laws and regulations</li>
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-semibold mb-3">5. User Responsibilities</h3>
                <ul className="list-disc list-inside space-y-2">
                  <li>Maintain the security and confidentiality of your account credentials</li>
                  <li>Promptly report any unauthorized access or security incidents</li>
                  <li>Comply with all organizational policies and procedures</li>
                  <li>Use device management features responsibly and only for legitimate purposes</li>
                  <li>Keep enrolled devices secure and report lost or stolen devices immediately</li>
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-semibold mb-3">6. Prohibited Activities</h3>
                <ul className="list-disc list-inside space-y-2">
                  <li>Attempting to bypass or disable management profiles</li>
                  <li>Sharing account credentials with unauthorized individuals</li>
                  <li>Using device management features for personal gain or malicious purposes</li>
                  <li>Interfering with the proper operation of the management system</li>
                  <li>Violating any applicable laws or regulations</li>
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-semibold mb-3">7. Service Availability</h3>
                <p>
                  While we strive to maintain continuous service availability, the device management 
                  portal may experience temporary interruptions for maintenance, updates, or due to 
                  circumstances beyond our control. We do not guarantee uninterrupted access to the service.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-semibold mb-3">8. Limitation of Liability</h3>
                <p>
                  The organization provides this device management service "as is" without warranties 
                  of any kind. Users assume responsibility for any data loss or device issues that may 
                  occur during the management process.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-semibold mb-3">9. Changes to Terms</h3>
                <p>
                  These terms may be updated periodically to reflect changes in our practices or 
                  applicable laws. Users will be notified of significant changes via email or through 
                  the portal interface.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-semibold mb-3">10. Contact Information</h3>
                <p>
                  For questions about these terms or the device management service, please contact:
                  <br />
                  <strong>Email:</strong> support@smplify.app
                  <br />
                  <strong>Portal:</strong> Administrative contact through main portal system
                </p>
              </section>

              <div className="bg-muted p-4 rounded-lg mt-8">
                <p className="text-sm">
                  <strong>Last Updated:</strong> {new Date().toLocaleDateString()}
                  <br />
                  By proceeding with account registration, you acknowledge that you have read, 
                  understood, and agree to be bound by these terms and conditions.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TermsAndConditionsPage;